<template>
  <div class="system-settings-container">
    <!-- 顶部标题栏 -->
    <div class="settings-header">
      <div class="header-left">
        <i class="el-icon-s-tools header-icon"></i>
        <h2 class="settings-title">系统参数配置</h2>
        <span class="settings-desc">共 {{ systemList.length }} 个参数配置项</span>
      </div>
      <div class="header-right">
        <el-button type="primary" icon="el-icon-plus" @click="dialogVisible = true" v-if="userInfo.clinicId === 0" size="mini">
          新增参数
        </el-button>
        <el-button icon="el-icon-refresh" @click="loadData" :loading="loading" size="mini">
          刷新
        </el-button>
      </div>
    </div>

    <!-- 参数筛选栏 -->
    <div class="filter-bar">
      <el-radio-group v-model="filterType" size="small">
        <el-radio-button label="all">全部</el-radio-button>
        <el-radio-button label="0">文本参数</el-radio-button>
        <el-radio-button label="1">开关参数</el-radio-button>
        <el-radio-button label="2">时间参数</el-radio-button>
        <el-radio-button label="5">地址参数</el-radio-button>
      </el-radio-group>

      <el-input
          v-model="searchKeyword"
          placeholder="搜索参数名称..."
          prefix-icon="el-icon-search"
          size="small"
          style="width: 200px; margin-left: 16px;"
          clearable
      />
    </div>

    <!-- 表格展示参数 -->
    <el-table
        :data="filteredList"
        border
        stripe
        size="small"
        v-loading="loading"
        style="width: 100%"
        :row-class-name="tableRowClassName"
    >
      <el-table-column prop="name" label="参数名称" width="200" fixed="left">
        <template slot-scope="scope">
          <div class="param-name-cell">
            <i :class="getParamIcon(scope.row.type)" style="margin-right: 6px;"></i>
            <span>{{ scope.row.name }}</span>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="param" label="参数编码" width="150"></el-table-column>

      <el-table-column label="参数值" min-width="300">
        <template slot-scope="scope">
          <!-- 文本类型 -->
          <el-input
              v-if="scope.row.type === 0"
              v-model="scope.row.value"
              :type="isNumberParam(scope.row) ? 'number' : 'text'"
              size="small"
              @input="handleNumberInput($event, scope.row)"
              @blur="handleEdit(scope.row)"
              :disabled="scope.row.state === 0"
              clearable
          />
          <!-- 开关类型 -->
          <el-switch
              v-else-if="scope.row.type === 1"
              v-model="scope.row.value"
              active-value="1"
              inactive-value="0"
              active-color="#13ce66"
              inactive-color="#ff4949"
              @change="handleSwitchChange(scope.row)"
          />
          <!-- 时间类型 -->
          <el-time-picker
              v-else-if="scope.row.type === 2"
              v-model="scope.row.value"
              format="HH:mm:ss"
              value-format="HH:mm:ss"
              size="small"
              placeholder="选择时间"
              @change="handleEdit(scope.row)"
              style="width: 100%"
              :disabled="scope.row.state === 0"
          />
          <!-- 地址类型：级联选择器，placeholder 动态显示完整路径 -->
          <el-cascader
              v-else-if="scope.row.type === 5"
              v-model="addressValues[scope.row.id]"
              :props="addressProps"
              :options="provinces"
              @change="(val) => handleAddressChange(scope.row, val)"
              :placeholder="addressPlaceholder[scope.row.id] || '请选择地址'"
              size="small"
              style="width: 100%"
              :show-all-levels="true"
              :disabled="scope.row.state === 0"
              clearable
          />
          <span v-else>{{ scope.row.value || '—' }}</span>
        </template>
      </el-table-column>

      <el-table-column label="状态" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="scope.row.state == 1 ? 'success' : 'danger'" size="small">
            {{ scope.row.state == 1 ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="分类" width="100" align="center">
        <template slot-scope="scope">
          {{ getCateLabel(scope.row.cate) }}
        </template>
      </el-table-column>

      <el-table-column label="更新时间" width="160" align="center">
        <template slot-scope="scope">
          {{ formatTime(scope.row.updateTime) }}
        </template>
      </el-table-column>

      <el-table-column label="操作" width="120" fixed="right" align="center">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="handleEditState(scope.row)">
            {{ scope.row.state == 1 ? '禁用' : '启用' }}
          </el-button>
          <el-button type="text" size="small" @click="showParamDetail(scope.row)">详情</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 空状态 -->
    <div v-if="filteredList.length === 0 && !loading" class="empty-state">
      <i class="el-icon-document"></i>
      <p>暂无参数数据</p>
    </div>

    <!-- 新增参数弹窗 -->
    <el-dialog
        title="新增系统参数"
        :visible.sync="dialogVisible"
        width="500px"
        :close-on-click-modal="false"
        class="settings-dialog"
        @closed="addEmptyForm"
    >
      <el-form :model="addForm" size="small" label-width="100px" :rules="rules" ref="addForm" class="dialog-form">
        <el-form-item label="参数名称:" prop="name">
          <el-input v-model="addForm.name" placeholder="请输入参数名称" @input="generateParamCode" prefix-icon="el-icon-edit" />
        </el-form-item>
        <el-form-item label="参数类型:" prop="type">
          <el-select v-model="addForm.type" style="width: 100%">
            <el-option v-for="item in typeOptions" :key="item.id" :label="item.label" :value="item.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="参数拼音:" prop="param">
          <el-input disabled v-model="addForm.param" placeholder="自动生成" prefix-icon="el-icon-document" />
        </el-form-item>
        <el-form-item label="参数值:" prop="value">
          <el-input v-model="addForm.value" placeholder="请输入参数值" prefix-icon="el-icon-edit" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false" size="small">取 消</el-button>
        <el-button type="primary" @click="submitAddForm" size="small">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 参数详情弹窗 -->
    <el-dialog title="参数详情" :visible.sync="detailVisible" width="500px" class="param-detail-dialog">
      <div class="param-detail-content" v-if="currentDetail">
        <div class="detail-item"><label>参数名称：</label><span>{{ currentDetail.name }}</span></div>
        <div class="detail-item"><label>参数编码：</label><span>{{ currentDetail.param }}</span></div>
        <div class="detail-item"><label>参数值：</label><span>{{ currentDetail.value || '未设置' }}</span></div>
        <div class="detail-item"><label>参数类型：</label><el-tag :type="getTypeTagType(currentDetail.type)" size="small">{{ getTypeLabel(currentDetail.type) }}</el-tag></div>
        <div class="detail-item"><label>状态：</label><el-tag :type="currentDetail.state == 1 ? 'success' : 'danger'" size="small">{{ currentDetail.state == 1 ? '启用' : '禁用' }}</el-tag></div>
        <div class="detail-item"><label>最后更新：</label><span>{{ formatTime(currentDetail.updateTime) }}</span></div>
      </div>
      <div slot="footer"><el-button @click="detailVisible = false" size="small">关 闭</el-button></div>
    </el-dialog>
  </div>
</template>

<script>
import { getSystemList, saveSystemData, updateSystemData } from "@/api/systemParameter/systemParameter"
import { pinyin } from "pinyin-pro"
import { getArea } from "@/api/reservation/reservation"
import { EventBus } from "@/directive/event-bus.js"

export default {
  name: 'SystemSettings',
  data() {
    return {
      permissionsArr: [],
      provinces: [],
      addressProps: {
        lazy: true,
        value: 'areaCode',
        label: 'areaName',
        emitPath: true,
        leaf: node => node.areaLevel >= 5,
        lazyLoad: async (node, resolve) => {
          try {
            const level = node.level + 1;
            if (level > 5) return resolve([]);
            const params = {
              areaCode: node.level > 0 ? node.data.areaCode : undefined,
              areaLevel: level,
              condtion: ['area_p_code', 'area_p_code', 'area_c_code', 'area_d_code', 'area_t_code'][level - 1]
            };
            const res = await getArea(params);
            const responseData = res?.data || [];
            const formattedData = responseData.map(item => ({
              ...item,
              value: item.areaCode,
              label: item.areaName,
              level: level,
              isLeaf: level >= 5
            }));
            resolve(formattedData);
          } catch (error) {
            resolve([]);
          }
        }
      },
      addressValues: {},        // 存储每个地址参数的地址码数组
      addressPlaceholder: {},   // 存储每个地址参数的 placeholder 字符串（拼接路径）
      loading: false,
      systemList: [],
      dialogVisible: false,
      detailVisible: false,
      currentDetail: null,
      filterType: 'all',
      searchKeyword: '',
      addForm: {
        name: '',
        param: '',
        value: '',
        clinicId: null,
        type: null,
      },
      rules: {
        name: [{ required: true, message: '请输入参数名称', trigger: 'blur' }],
        param: [{ required: true, message: '请输入拼音缩写', trigger: 'blur' }],
        value: [{ required: true, message: '请输入参数值', trigger: 'blur' }]
      },
      typeOptions: [
        { id: 0, label: '文本参数' },
        { id: 1, label: '开关参数' },
        { id: 2, label: '时间参数' },
        { id: 3, label: '单选框' },
        { id: 4, label: '下拉框' },
        { id: 5, label: '地址' }
      ],
      userInfo: {},
    }
  },
  computed: {
    filteredList() {
      let list = this.systemList
      if (this.filterType !== 'all') {
        list = list.filter(item => item.type == this.filterType)
      }
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase()
        list = list.filter(item =>
            item.name.toLowerCase().includes(keyword) ||
            (item.param && item.param.toLowerCase().includes(keyword))
        )
      }
      // 排序：开关(type=1) → 文本(type=0) → 其他
      const order = { 1: 0, 0: 1 };
      return list.sort((a, b) => {
        const orderA = order[a.type] !== undefined ? order[a.type] : 2;
        const orderB = order[b.type] !== undefined ? order[b.type] : 2;
        if (orderA !== orderB) return orderA - orderB;
        // 同类型保持原顺序（按 id 或 name）
        return a.id - b.id;
      });
    }
  },
  created() {
    this.loadData();
    this.permissionsArr = JSON.parse(localStorage.getItem('permissions')) || []
    this.userInfo = JSON.parse(localStorage.getItem('userInfo')) || {}
  },
  methods: {
    // 根据地址码数组动态从接口获取各级名称并拼接路径（不依赖本地树，确保完整显示）
    async getAddressPathString(codes) {
      if (!codes || codes.length === 0) return '';
      const names = [];
      for (let i = 0; i < codes.length; i++) {
        const code = codes[i];
        const level = i + 1;
        const parentCode = i === 0 ? undefined : codes[i - 1];
        const params = {
          areaCode: parentCode,
          areaLevel: level,
          condtion: ['area_p_code', 'area_p_code', 'area_c_code', 'area_d_code', 'area_t_code'][level - 1]
        };
        try {
          const res = await getArea(params);
          const node = (res.data || []).find(item => String(item.areaCode) === String(code));
          if (node) {
            names.push(node.areaName);
          } else {
            console.warn(`未找到地址编码对应的名称: ${code}`);
            break;
          }
        } catch (error) {
          console.error(`获取地址层级失败: 编码 ${code}`, error);
          break;
        }
      }
      return names.join(' / ');
    },
    async updateAddressPlaceholder(itemId, codes) {
      if (codes && codes.length) {
        const pathStr = await this.getAddressPathString(codes);
        this.$set(this.addressPlaceholder, itemId, pathStr);
      } else {
        this.$set(this.addressPlaceholder, itemId, '');
      }
    },
    // 地址变更处理
    async handleAddressChange(item, val) {
      this.addressValues[item.id] = val || [];
      await this.updateAddressPlaceholder(item.id, val);
      item.value = JSON.stringify(this.addressValues[item.id]);
      this.handleEdit(item);
    },
    // 预加载地址路径（用于构建树，方便级联选择器展开）
    async preloadAddressPath(codes) {
      if (!codes || codes.length === 0) return;
      if (this.provinces.length === 0) {
        await this.loadProvincial();
      }
      let currentLevel = this.provinces;
      for (let i = 0; i < codes.length; i++) {
        const code = String(codes[i]);
        let targetNode = currentLevel.find(n => String(n.areaCode) === code);
        if (!targetNode) {
          const level = i + 1;
          const parentCode = i === 0 ? undefined : codes[i-1];
          const params = {
            areaCode: parentCode,
            areaLevel: level,
            condtion: ['area_p_code', 'area_p_code', 'area_c_code', 'area_d_code', 'area_t_code'][level - 1]
          };
          const res = await getArea(params);
          const newNodes = (res.data || []).map(item => ({
            ...item,
            value: item.areaCode,
            label: item.areaName,
            level: level,
            isLeaf: level >= 5
          }));
          if (i === 0) {
            this.provinces = newNodes;
            currentLevel = this.provinces;
            targetNode = currentLevel.find(n => String(n.areaCode) === code);
          } else {
            const parent = this.findNodeByCode(codes[i-1], this.provinces);
            if (parent) {
              this.$set(parent, 'children', newNodes);
              currentLevel = parent.children;
              targetNode = currentLevel.find(n => String(n.areaCode) === code);
            }
          }
        }
        if (!targetNode) break;
        if (i < codes.length - 1 && (!targetNode.children || targetNode.children.length === 0)) {
          const nextLevel = i + 2;
          const params = {
            areaCode: code,
            areaLevel: nextLevel,
            condtion: ['area_p_code', 'area_p_code', 'area_c_code', 'area_d_code', 'area_t_code'][nextLevel - 1]
          };
          const res = await getArea(params);
          const childrenData = (res.data || []).map(item => ({
            ...item,
            value: item.areaCode,
            label: item.areaName,
            level: nextLevel,
            isLeaf: nextLevel >= 5
          }));
          this.$set(targetNode, 'children', childrenData);
        }
        currentLevel = targetNode.children || [];
      }
    },
    // 辅助方法：根据编码在树中查找节点
    findNodeByCode(code, tree) {
      for (const node of tree) {
        if (String(node.areaCode) === String(code)) return node;
        if (node.children && node.children.length) {
          const found = this.findNodeByCode(code, node.children);
          if (found) return found;
        }
      }
      return null;
    },
    async loadProvincial() {
      try {
        const res = await getArea({ areaLevel: 1, condtion: 'area_p_code' });
        this.provinces = (res.data || []).map(item => ({
          areaCode: String(item.areaCode),
          areaName: item.areaName,
          areaLevel: 1,
          leaf: false,
        }));
      } catch (error) {
        console.error('加载省级数据失败:', error);
      }
    },
    handleNumberInput(event, item) {
      if (this.isNumberParam(item)) {
        const value = event.target.value.replace(/\D/g, '');
        item.value = value;
        event.target.value = value;
      }
    },
    isNumberParam(item) {
      return item.param === 'is_blood_number' || item.param === 'is_blood_pressure';
    },
    handleCommand(command, item) {
      if (command === 'toggle') {
        this.handleEditState(item)
      } else if (command === 'detail') {
        this.showParamDetail(item)
      }
    },
    getParamIcon(type) {
      const icons = {
        0: 'el-icon-edit',
        1: 'el-icon-switch-button',
        2: 'el-icon-time',
        5: 'el-icon-location-outline'
      }
      return icons[type] || 'el-icon-setting'
    },
    getTypeTagType(type) {
      const types = {
        0: 'primary',
        1: 'success',
        2: 'warning'
      }
      return types[type] || 'info'
    },
    getCateLabel(cate) {
      const map = { 1: '基础设置', 2: '医保', 3: '财务', 4: '排班', 5: '库存' }
      return map[cate] || '其他'
    },
    formatTime(time) {
      if (!time) return '--'
      return new Date(time).toLocaleString()
    },
    showParamDetail(item) {
      this.currentDetail = item
      this.detailVisible = true
    },
    getTypeLabel(type) {
      const types = {
        0: '文本参数',
        1: '开关参数',
        2: '时间参数'
      }
      return types[type] || '未知类型'
    },
    tableRowClassName({ row }) {
      if (row.state === 0) return 'disabled-row'
      return ''
    },
    async loadData() {
      this.loading = true;
      try {
        const res = await getSystemList();
        this.systemList = res.data;

        // 解析地址参数
        let tempAddressValues = {};
        for (const item of this.systemList) {
          if (item.type === 5 && item.value) {
            try {
              let arr = JSON.parse(item.value);
              if (Array.isArray(arr)) {
                tempAddressValues[item.id] = arr;
              } else {
                tempAddressValues[item.id] = [];
              }
            } catch (e) {
              tempAddressValues[item.id] = [];
            }
          }
        }

        // 预加载所有地址路径数据（用于级联选择器树结构）
        for (const item of this.systemList) {
          if (item.type === 5 && tempAddressValues[item.id] && tempAddressValues[item.id].length) {
            await this.preloadAddressPath(tempAddressValues[item.id]);
          }
        }

        // 赋值地址码数组
        this.addressValues = tempAddressValues;

        // 生成 placeholder 路径字符串（动态接口获取，确保完整）
        for (const item of this.systemList) {
          if (item.type === 5) {
            const codes = this.addressValues[item.id];
            if (codes && codes.length) {
              await this.updateAddressPlaceholder(item.id, codes);
            } else {
              this.$set(this.addressPlaceholder, item.id, '');
            }
          }
        }

        // 事件通知
        const stockSwitch = this.systemList.find(item => item.name === '库房开关');
        if (stockSwitch) {
          EventBus.$emit('stock-management-visibility-change', stockSwitch.value === '1');
        }
        const msfxSwitch = this.systemList.find(item =>
            item.name === '码上放心开关' || item.param === 'mafx_switch'
        );
        if (msfxSwitch) {
          EventBus.$emit('msfx-switch-change', msfxSwitch.value === '1');
        }

        this.$store.commit('preload/updatePreloadData', {
          key: 'getSystemListOptions',
          data: res.data
        });

        this.$message.success('数据加载成功');
      } catch (error) {
        console.error('加载数据失败:', error);
        this.$message.error('获取参数列表失败');
      } finally {
        this.loading = false;
      }
    },
    async handleEdit(item) {
      if (!item.value && item.type !== 1) {
        this.$message.warning('请输入参数值')
        return
      }
      try {
        const res = await updateSystemData(item)
        if (res.code === 200) {
          this.$message.success('参数更新成功')
          if (item.name === '库房开关') {
            EventBus.$emit('stock-management-visibility-change', item.value === '1');
          }
          if (item.name === '码上放心开关' || item.param === 'mafx_switch') {
            EventBus.$emit('msfx-switch-change', item.value === '1');
          }
        } else {
          this.$message.error(res.message || '更新失败')
        }
      } catch (error) {
        console.error('更新参数失败:', error)
        this.$message.error('更新失败')
      }
    },
    async handleSwitchChange(item) {
      try {
        const res = await updateSystemData(item)
        if (res.code === 200) {
          this.$message.success(item.state == 1 ? '参数已启用' : '参数已禁用')
          if (item.name === '库房开关') {
            EventBus.$emit('stock-management-visibility-change', item.value === '1');
          }
          if (item.name === '码上放心开关' || item.name === '马上放心开关' || item.param === 'mafx_switch') {
            EventBus.$emit('msfx-switch-change', item.value === '1');
          }
        } else {
          item.state = item.state == 1 ? 0 : 1
          this.$message.error(res.message || '操作失败')
        }
      } catch (error) {
        item.state = item.state == 1 ? 0 : 1
        this.$message.error('操作失败')
      }
    },
    async handleEditState(item) {
      if (item.type === 1) {
        item.state = item.state == 1 ? 0 : 1
        await this.handleSwitchChange(item)
      } else {
        item.state = item.state == 1 ? 0 : 1
        await this.handleEdit(item)
      }
    },
    addEmptyForm() {
      this.addForm = Object.assign({}, this.$options.data.call(this).addForm)
      if (this.$refs.addForm) {
        this.$refs.addForm.resetFields()
      }
    },
    generateParamCode() {
      if (!this.addForm.name) {
        this.addForm.param = ''
        return
      }
      let code = this.addForm.name.replace(/\s+/g, '')
      this.addForm.param = pinyin(code, {
        pattern: 'first',
        toneType: 'none',
        separator: ''
      }).toLowerCase()
    },
    submitAddForm() {
      this.$refs.addForm.validate(async (valid) => {
        if (!valid) {
          this.$message.warning('请填写完整的参数信息')
          return
        }
        try {
          const res = await saveSystemData(this.addForm)
          if (res.code === 200 || res.code) {
            this.$message.success('添加参数成功!')
            this.dialogVisible = false
            await this.loadData()
          } else {
            this.$message.error(res.message || '添加参数失败!')
          }
        } catch (error) {
          console.error('添加参数失败:', error)
          this.$message.error('添加参数失败!')
        }
      })
    }
  }
}
</script>

<style lang="less" scoped>
/* 样式与之前完全一致，无需修改 */
.system-settings-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
}
.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  .header-left {
    display: flex;
    align-items: center;
    .header-icon {
      font-size: 24px;
      color: #409EFF;
      margin-right: 12px;
    }
    .settings-title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }
    .settings-desc {
      margin-left: 16px;
      color: #909399;
      font-size: 13px;
    }
  }
  .header-right {
    display: flex;
    gap: 12px;
  }
}
.filter-bar {
  background: #fff;
  padding: 12px 20px;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}
.param-name-cell i {
  font-size: 14px;
  color: #409eff;
}
.disabled-row {
  background-color: #f9f9f9;
  color: #aaa;
}
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border-radius: 8px;
  i {
    font-size: 48px;
    color: #dcdfe6;
    margin-bottom: 16px;
  }
  p {
    color: #909399;
  }
}
.param-detail-content .detail-item {
  display: flex;
  margin-bottom: 12px;
  label {
    width: 90px;
    font-weight: 600;
    color: #606266;
  }
  span {
    flex: 1;
    color: #303133;
  }
}
.settings-dialog, .param-detail-dialog {
  .el-dialog__header {
    background: linear-gradient(135deg, #409EFF 0%, #66b1ff 100%);
    padding: 12px 20px;
    .el-dialog__title {
      color: #fff;
      font-weight: 600;
    }
  }
}
@media (max-width: 768px) {
  .system-settings-container {
    padding: 12px;
  }
  .settings-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .filter-bar {
    flex-wrap: wrap;
  }
}
</style>